import React from "react";
import dayjs from "dayjs";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { AuditTradeItemClass } from "@moonscape/shared";

import { TradeItemSmall } from "./TradeItemSmall";
import { TradeItemMedium } from "./TradeItemMedium";
import { LedgerCol, LedgerFullRow } from "./ledgerTable";

const utc = require("dayjs/plugin/utc");
dayjs.extend(utc);

export interface TradeItemProps {
  id: string;
  tradeid: string;
  type: string;
  exchange: string;
  timestamp: string;
  datetime: string;
  buyasset: string;
  buyqty: string;
  sellasset: string;
  sellqty: string;
  feeqty: string;
  feeasset: string;
  wallet: string;
  side: string;
  userId: string;
  screen: "sm" | "md" | "lg";
  // for showing emtpy row i.e. no results
  isEmpty: boolean;
  text: string;
  isLoading: boolean;
}

export enum TxnType {
  DEPOSIT = "deposit",
  WITHDRAWAL = "withdrawal",
  TRADE = "trade",
  BUY = "buy",
  SELL = "sell",
  INTEREST = "interest",
  STAKING_REWARD = "staking reward",
  ADVANCED_TRADE_FILL = "advanced trade fill",
}

export enum FormattedTxnType {
  DEPOSIT = "Deposit",
  WITHDRAWAL = "Withdrawal",
  TRADE = "Trade",
  BUY = "Buy",
  SELL = "Sell",
  INTEREST = "Interest",
  ADVANCED_TRADE_FILL = "Advanced Trade Fill",
}

/** The ledger's columns. Everything after "Transaction" appears from 900px. */
export const TRADE_COLS: LedgerCol[] = [
  { width: 34 },
  { label: "Transaction" },
  { label: "Date" },
  { label: "Source" },
  { label: "Quantity", right: true },
  { label: "Fee", right: true },
  { label: "Flag" },
  { label: "Value", right: true },
  { width: 34 },
];

export const TradeItem = (props) => {
  const {
    id,
    side,
    userId,
    screen, // if screen is sm for breakpoints
    isEmpty = false,
    text,
    isQueryLoading,
  }: TradeItemProps = props;

  const t = new AuditTradeItemClass(props);

  const EmptyStateText = ({ title }: { title: string }) => (
    <div className="flex flex-col items-center justify-center p-10 text-center">
      <MagnifyingGlassIcon className="h-7 w-7 text-ms-dim" aria-hidden="true" />
      <h2 className="mt-4 text-[17px] font-semibold tracking-[-0.02em] text-ms-ink">
        {title}
      </h2>
      <p className="mt-1 text-[13px] text-ms-mute">
        There are no transactions to display.
      </p>
    </div>
  );

  // empty state - no results
  if (isEmpty === true && screen === "sm") {
    return (
      // sm screen
      <div className="flex-1 px-4 py-2 text-left">
        <EmptyStateText title={text || "No transactions"} />
      </div>
    );
    // md screen
  } else if (isEmpty === true && screen === "md") {
    return (
      <LedgerFullRow span={TRADE_COLS.length}>
        <EmptyStateText title={text || "No transactions"} />
      </LedgerFullRow>
    );
  }

  // for sm screen
  if (screen === "sm") {
    return <TradeItemSmall t={t} isQueryLoading={isQueryLoading} />;
  } else {
    // for 'md' and larger screens
    return <TradeItemMedium t={t} isQueryLoading={isQueryLoading} />;
  }
};
